import { Model } from '../../infra/model'
import {
  RoomSessionDto,
  RoomSessionStatusDto,
  RoomSessionParticipantDto,
  RoomSessionParticipantStatusDto,
} from '../dtos/rooms'
import { Partial } from 'react-spring'
import { Id } from '../../types/definitions'
import { DateZero } from '../../utils/date-util'
import { collection } from '../../infra/collection'
import { PrimeMemberByPublicId } from '../../iam/MemberStore'
import Member from '../../iam/models/Member'

export class RoomSession extends Model<RoomSessionDto, RoomSession> {
  id = Id.none
  dateStarted = DateZero()
  dateModified = DateZero()
  status = RoomSessionStatus.Unknown

  participants = collection<RoomSessionParticipantDto, RoomSessionParticipant>({
    idAttribute: 'userPublicId',
    create: () =>
      new RoomSessionParticipant(
        this.conversationId,
        this.primeMemberByPublicId,
      ),
  })

  constructor(
    private conversationId: Id,
    private primeMemberByPublicId: PrimeMemberByPublicId,
  ) {
    super()
  }

  parse(attrs: Partial<RoomSessionDto>): Partial<RoomSession> {
    return {
      id: attrs.id,
      dateStarted: attrs.date_started,
      dateModified: attrs.date_modified,
      status: RoomSession.mapRoomSessionStatus(attrs.status),
    }
  }

  private static mapRoomSessionStatus(
    status: RoomSessionStatusDto | undefined,
  ): RoomSessionStatus {
    switch (status) {
      case RoomSessionStatusDto.Active:
        return RoomSessionStatus.Active
      case RoomSessionStatusDto.Ended:
        return RoomSessionStatus.Ended
      default:
        return RoomSessionStatus.Unknown
    }
  }
}

export const enum RoomSessionStatus {
  Unknown = 'Unknown',
  Active = 'Active',
  Ended = 'Ended',
}

export class RoomSessionParticipant extends Model<
  RoomSessionParticipantDto,
  RoomSessionParticipant
> {
  userPublicId = Id.none
  member = Member.none
  dateConnected = DateZero()
  dateDisconnected = DateZero()
  status = RoomSessionParticipantStatus.Unknown

  constructor(
    private workspaceId: Id,
    private primeMemberByPublicId: PrimeMemberByPublicId,
  ) {
    super()
  }

  parse(attrs: Partial<RoomSessionParticipantDto>) {
    return {
      userPublicId: attrs.user_public_id,
      member: this.primeMemberByPublicId({
        workspace_id: this.workspaceId,
        user_public_id: attrs.user_public_id,
      }),
      status: RoomSessionParticipant.mapRoomSessionParticipantStatus(
        attrs.status,
      ),
      dateConnected: attrs.date_connected,
      dateDisconnected: attrs.date_disconnected,
    }
  }

  private static mapRoomSessionParticipantStatus(
    status: RoomSessionParticipantStatusDto | undefined,
  ): RoomSessionParticipantStatus {
    switch (status) {
      case RoomSessionParticipantStatusDto.Connected:
        return RoomSessionParticipantStatus.Connected
      case RoomSessionParticipantStatusDto.Disconnected:
        return RoomSessionParticipantStatus.Disconnected
      default:
        return RoomSessionParticipantStatus.Unknown
    }
  }
}

export const enum RoomSessionParticipantStatus {
  Unknown = 'Unknown',
  Connected = 'Connected',
  Disconnected = 'Disconnected',
}
